<template>
  <dt-avatar
    :size="size"
    :fullName="fullName"
    :presence="presence"
    :imageSrc="imageSrc || ''"
    :imageAlt="imageAlt || ''"
    :color="avatarColor"
    :seed="seed"
    :avatarClass="mergedAvatarClass"
    :canvasClass="mergedCanvasClass"
    :group="group"
    class="cursor-pointer"
  />
</template>

<script setup lang="ts">
import { computed, useAttrs } from 'vue';
import { DtAvatar } from '@dialpad/dialtone/vue3';

const attrs = useAttrs();

const props = defineProps<{
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | string;
  fullName: string;
  presence?: string;
  avatarColor?: string;
  imageSrc?: string;
  imageAlt: string;
  showLabel?: boolean;
  avatarClass?: string;
  canvasClass?: string;
  id?: string; // Optional ID to use as seed for deterministic color generation
  group?: number;
}>();

//  Default to MD avatar size if not specified
const size = computed(() => props.size || 'md');

// The Dialtone avatar component supports color codes like '100', '200', etc.
// We need to pass the avatarColor prop directly to the color prop of the DtAvatar component
const avatarColor = computed(() => props.avatarColor);

// Use the ID as the seed if available, otherwise fall back to the full name
const seed = computed(() => props.id || props.fullName);

//  Merge Avatar classes together
const mergedAvatarClass = computed(() => {
  return ['dp3-avatar', props.avatarClass].filter(Boolean).join(' ');
});

//  Merge Avatar canvas classes together
const mergedCanvasClass = computed(() => {
  return ['dp3-avatar--canvas', props.canvasClass].filter(Boolean).join(' ');
});

// Map to Dialtone presence
const presence = computed(() => {
  switch (props.presence) {
    case 'active':
    case 'available':
      return 'active';
    case 'away':
      return 'away';
    case 'busy':
    case 'busy-call':
    case 'busy-meeting':
    case 'dnd':
      return 'busy';
    case 'offline':
      return 'offline';
    default:
      return undefined;
  }
});
</script>

<style lang="less">
.dp3-avatar.d-avatar--xl:has(.d-presence) .dp3-avatar--canvas {
  clip-path: path('M 0 0 L 96 0 L 96 76.32 L 81.6 76.32 Q 76.32 76.32 76.32 81.6 L 76.32 96 L 0 96 Z');
}
.dp3-avatar.d-avatar--lg:has(.d-presence) .dp3-avatar--canvas {
  clip-path: path('M 0 0 L 42 0 L 42 28.875 L 32.55 28.875 Q 28.875 28.875 28.875 32.55 L 28.875 42 L 0 42 Z');
}
.dp3-avatar.d-avatar--md:has(.d-presence) .dp3-avatar--canvas {
  clip-path: path('M 0 0 L 36 0 L 36 22.86 L 27 22.86 Q 22.86 22.86 22.86 27 L 22.86 36 L 0 36 Z');
}
.dp3-avatar.d-avatar--sm:has(.d-presence) .dp3-avatar--canvas {
  clip-path: path('M 0 0 L 20 0 L 20 11 L 14 11 Q 11 11 11 14 L 11 20 L 0 20 Z');
  border-radius: var(--dt-size-radius-300) !important;
}
.dp3-avatar.d-avatar--xs:has(.d-presence) .dp3-avatar--canvas {
  clip-path: path('M 0 0 L 16 0 L 16 8.8 L 11.2 8.8 Q 8.8 8.8 8.8 11.2 L 8.8 16 L 0 16 Z');
  border-radius: var(--dt-size-200) !important;
}
.dp3-avatar .d-presence {
  right: 0px !important;
  bottom: 0px !important;
}
.dp3-avatar .d-presence__inner {
  border: none !important;
  border-radius: 1.5px !important;
}
//  Remap status colors
html[data-dt-theme='light'] {
  .dp3-avatar.d-presence {
    --presence-color-background-active: var(--dt-color-green-500);
    --presence-color-background-away: var(--dt-color-gold-400);
    --presence-color-background-busy: var(--dt-color-red-400);
  }
}
html[data-dt-theme='dark'] {
  .dp3-avatar.d-presence {
    --presence-color-background-active: var(--dt-color-green-425);
    --presence-color-background-away: var(--dt-color-gold-500);
    --presence-color-background-busy: var(--dt-color-red-500);
  }
}

.dp3-avatar--canvas {
  &,
  .d-avatar__image,
  &.d-avatar__canvas {
    border-radius: var(--dt-size-350);
  }

  .d-avatar--xs & {
    border-radius: var(--dt-size-200);
  }

  .dp3-avatar--xl & {
    --avatar-size-shape: var(--dt-size-750);
    border-radius: var(--dt-size-400);

    .d-avatar__initials {
      --avatar-size-text: var(--dp3-font-size-500);
    }
  }
}

//  Update avatar sizes where needed
.dp3-avatar {
  align-self: center;

  .dp3-user-menu & {
    --avatar-size-shape: 3.6rem; // Magic numbers are magic
  }

  .d-avatar__presence {
    bottom: calc(var(--presence-border-size) * -1);
    right: calc(var(--presence-border-size) * -1);
  }
  .d-presence {
    --presence-size: calc(var(--dt-size-100) * 10);
    --presence-color-border-base: var(--dp3-background-color);
    --presence-border-size: calc(var(--dt-size-100) + var(--dt-size-200));
    --presence-color-background-active: #00a010;
    --presence-color-background-busy: #ff1356;
    --presence-color-background-away: #ff9e0e;

    border-radius: 0;
    border: none;

    .dp3-content--wrapper & {
      --presence-color-border-base: var(--dt-color-surface-primary);
    }

    &__inner {
      box-sizing: content-box;
      border-radius: var(--dt-size-300);
      border: var(--presence-border-size) solid var(--presence-color-border-base);
    }
  }
}
</style>
